class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.lockedItemEnforcementTimer = 0;
    this.lockedItemQuantityUpdateInFlight = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.onCartOpen = (event) => {
      const detail = event?.detail || {};
      let triggeredBy = detail.triggeredBy;

      if (!triggeredBy && detail.triggeredBySelector) {
        triggeredBy = document.querySelector(detail.triggeredBySelector);
      }

      this.open(triggeredBy);
    };

    this.onCartClose = () => {
      this.close();
    };

    document.addEventListener('cart:open', this.onCartOpen);
    document.addEventListener('cart:close', this.onCartClose);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:open', this.onCartOpen);
    document.removeEventListener('cart:close', this.onCartClose);
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, .component-navbar__cart-link');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    document.dispatchEvent(new CustomEvent('cart:opened'));
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.scheduleLockedItemEnforcement('open');
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      // 当我们在外部点击加购或者打开购物车时，如果它还是处于空的状态但其实里边已经有东西了
      // 我们可以顺便在这里再移除一次 is-empty 以防万一
      const itemsWrap = this.querySelector('cart-drawer-items');
      if (itemsWrap && !itemsWrap.classList.contains('is-empty')) {
        this.classList.remove('is-empty');
      }
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const activeElement = document.activeElement;
        const userFocusedElement =
          activeElement &&
          activeElement !== document.body &&
          containerToTrapFocusOn?.contains(activeElement)
            ? activeElement
            : null;
        const focusElement = userFocusedElement || this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    
    // 如果有商品，移除自身的 is-empty
    if (parsedState.item_count > 0) {
      this.classList.remove('is-empty');
    }
      
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.scheduleLockedItemEnforcement('render-contents');
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  scheduleLockedItemEnforcement(source = 'unknown') {
    window.clearTimeout(this.lockedItemEnforcementTimer);
    this.lockedItemEnforcementTimer = window.setTimeout(() => {
      this.lockedItemEnforcementTimer = 0;
      this.enforceLockedItems(source);
    }, 0);
  }

  getLockedDrawerItems() {
    return Array.from(this.querySelectorAll('[data-quantity-locked="true"]'));
  }

  enforceLockedItemControls() {
    this.getLockedDrawerItems().forEach((item) => {
      const quantityInput = item.querySelector('.quantity__input');
      const minusButton = item.querySelector(".quantity__button[name='minus']");
      const plusButton = item.querySelector(".quantity__button[name='plus']");

      if (quantityInput) {
        quantityInput.setAttribute('min', '1');
        quantityInput.setAttribute('data-min', '1');
        quantityInput.setAttribute('max', '1');
        quantityInput.setAttribute('step', '1');
        quantityInput.setAttribute('readonly', 'readonly');
        quantityInput.setAttribute('disabled', 'disabled');
        quantityInput.setAttribute('aria-disabled', 'true');
      }

      [minusButton, plusButton].forEach((button) => {
        if (!button) return;
        button.disabled = true;
        button.classList.add('disabled');
        button.setAttribute('aria-disabled', 'true');
      });
    });
  }

  enforceLockedItems(source = 'unknown') {
    this.enforceLockedItemControls();

    if (this.lockedItemQuantityUpdateInFlight) return;

    const violatingItem = this.getLockedDrawerItems().find((item) => {
      const quantityInput = item.querySelector('.quantity__input');
      const quantityValue = Number.parseInt(quantityInput ? quantityInput.value : '', 10);
      return Number.isFinite(quantityValue) && quantityValue > 1;
    });

    if (!violatingItem) return;

    const drawerItems = this.querySelector('cart-drawer-items');
    if (!drawerItems || typeof drawerItems.updateQuantity !== 'function') return;

    const quantityInput = violatingItem.querySelector('.quantity__input');
    const lineIndex = violatingItem.getAttribute('data-line-index');
    const variantId =
      violatingItem.getAttribute('data-variant-id') ||
      (quantityInput ? quantityInput.dataset.quantityVariantId : undefined);

    if (!lineIndex) return;

    this.lockedItemQuantityUpdateInFlight = true;
    drawerItems.updateQuantity(lineIndex, 1, 'minus', variantId);

    window.setTimeout(() => {
      this.lockedItemQuantityUpdateInFlight = false;
      this.enforceLockedItemControls();
      this.scheduleLockedItemEnforcement(`${source}-post-update`);
    }, 500);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
